import { useCallback, useEffect, useRef, useMemo } from 'react';
import { safeStorage } from '@/lib/safeStorage';
import { useUserPreferences } from '@/hooks/useUserPreferences';

// Pinned modules now persist in users.preferences.pinned_modules — see
// app/docs/decisions/0001-pinned-modules-storage.md (Option A).
//
// Favorites have no count limit; the sidebar controls only the preview size.
//
// Persisted shape: pageName strings (e.g. 'Facturas', 'CRMPipeline'). The
// Sidebar consumes the same identifiers. Stale pageNames stay harmless: the
// Sidebar simply skips pinned items that no longer exist in the navigation.

const STORAGE_KEY = 'verix_pinned_modules';
const DEFAULT_PINS = ['Facturas', 'MyTasks', 'Bancos'];

/**
 * One-shot localStorage → backend migration.
 * Removable after one release cycle (call site is the only place that still
 * touches localStorage for pins).
 */
function readLegacyPins() {
  const raw = safeStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    const filtered = parsed.filter((v) => typeof v === 'string');
    return filtered.length > 0 ? filtered : null;
  } catch {
    return null;
  }
}

function clearLegacyPins() {
  try { safeStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
}

export function usePinnedModules() {
  const { preferences, update, isLoading, isUpdating, isError } = useUserPreferences();
  const migratedRef = useRef(false);

  const pinsFromBackend = useMemo(() => Array.isArray(preferences?.pinned_modules)
    ? [...new Set(preferences.pinned_modules.filter(id => typeof id === 'string' && id.length > 0))]
    : null, [preferences?.pinned_modules]);

  // One-shot migration: if backend has nothing yet, see if there's a legacy
  // localStorage value, upload it, then clear local. Only runs once per mount.
  useEffect(() => {
    if (isLoading || migratedRef.current) return;
    migratedRef.current = true;

    if (pinsFromBackend === null) {
      const legacy = readLegacyPins();
      if (legacy) {
        update({ pinned_modules: legacy });
        clearLegacyPins();
      }
    }
  }, [isLoading, pinsFromBackend, update]);

  // What we expose to the UI:
  //   - if backend has a list (even empty) → that's the source of truth
  //   - otherwise (loading or never set) → fall back to defaults so the
  //     sidebar isn't empty for first-time users
  const pinned = pinsFromBackend ?? DEFAULT_PINS;

  const isBusy = isLoading || isUpdating || isError;

  const isPinned = useCallback(
    (pageName) => pinned.includes(pageName),
    [pinned],
  );

  const toggle = useCallback(
    (pageName) => {
      if (isBusy || !pageName) return;
      const next = pinned.includes(pageName)
        ? pinned.filter((p) => p !== pageName)
        : [...pinned, pageName];
      update({ pinned_modules: next });
    },
    [pinned, update, isBusy],
  );

  const move = useCallback((id, direction) => {
    if (isBusy || ![-1, 1].includes(direction)) return;
    const index = pinned.indexOf(id);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= pinned.length) return;
    const next = [...pinned];
    [next[index], next[target]] = [next[target], next[index]];
    update({ pinned_modules: next });
  }, [pinned, update, isBusy]);

  const reset = useCallback(() => {
    if (isBusy) return;
    update({ pinned_modules: DEFAULT_PINS });
  }, [update, isBusy]);

  return { pinned, isPinned, toggle, move, reset, isBusy, isUpdating };
}

export const __test = { STORAGE_KEY, DEFAULT_PINS };
